import {
    Box,
    Typography,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
} from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { ReactElement, useState } from 'react'
import { useAppSelector } from '../../../redux/hook'
import { updateApplicationSettings } from '../../../services/applications'
import { flattenEntityTemplates } from '../../../utils/EntityUtils'
import {
    IAnalyticsSettingsConfiguration,
    IAnalytcsSettings,
} from './interfaces'
import { useTranslation } from 'react-i18next'

const AnalyticsSettingsConfiguration = (
    props: IAnalyticsSettingsConfiguration,
): ReactElement => {
    const { t } = useTranslation()
    const {
        enable,
        analyticsEntityTemplateId,
        updateSettings,
        orgId,
        hasWriteAccess,
    } = props
    const navSlice = useAppSelector((state) => state.nav)
    const { navList } = navSlice
    const [settings, setSettings] = useState<IAnalytcsSettings>({
        enable,
        analyticsEntityTemplateId,
    })
    const entityTypes = flattenEntityTemplates(navList)
    const handleSave = async () => {
        updateSettings(settings)
        await updateApplicationSettings<IAnalytcsSettings>(
            orgId,
            'analytics',
            settings,
        )
        enqueueSnackbar('Settings saved successfully', { variant: 'success' })
    }
    return (
        <Box
            sx={{
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
            }}
        >
            <Box
                sx={{
                    width: '500px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('enableBI')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <Switch
                            checked={settings.enable === 'true'}
                            onChange={(event) =>
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        enable: event.target.checked.toString(),
                                    }
                                })
                            }
                            inputProps={{
                                'aria-label': t('enableanalytics'),
                                title: t('enableanalytics'),
                            }}
                            name="enableAnalytics"
                            id="enable-analytics"
                            disabled={!hasWriteAccess}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('bIEntityTemplate')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <FormControl fullWidth disabled={!hasWriteAccess}>
                            <InputLabel
                                id="choose-bi-entity-template"
                                htmlFor="choose-bi-entity-template-select"
                            >
                                {t('chooseTemplateLabel')}
                            </InputLabel>
                            <Select
                                id="choose-bi-entity-template-select"
                                labelId="choose-bi-entity-template"
                                label={t('chooseTemplateLabel')}
                                name="analyticsEntityTemplateId"
                                value={settings.analyticsEntityTemplateId || ''}
                                onChange={(event) =>
                                    setSettings((settings) => {
                                        return {
                                            ...settings,
                                            analyticsEntityTemplateId:
                                                event.target.value,
                                        }
                                    })
                                }
                                defaultValue={analyticsEntityTemplateId || ''}
                            >
                                {entityTypes?.map((option) => (
                                    <MenuItem
                                        key={option.id}
                                        value={option.id}
                                        dense
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                {hasWriteAccess && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            sx={{
                                backgroundColor: '#3a5e98',
                                color: 'white',
                            }}
                        >
                            {t('save')}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default AnalyticsSettingsConfiguration
